import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import { FC } from 'react';
import { Flex } from '@tremor/react';
import { Lock, LucideIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';

export interface IconLinkProps extends NavLinkProps {
  icon: LucideIcon;
  iconAltText: string;
  activeClassName?: string;
  isExpanded?: boolean;
  isSublink?: boolean;
  additionalTo?: string;
  disabled?: boolean;
  tooltip?: string;
}

export const IconLink: FC<IconLinkProps> = ({
  icon,
  children,
  className,
  isExpanded = true,
  isSublink,
  additionalTo,
  disabled = false,
  tooltip,
  ...rest
}) => {
  const location = useLocation();
  const Icon = icon;

  const navLink = (
    <NavLink
      className={({ isActive }) => {
        const isActiveLink = location.pathname === additionalTo || isActive;
        return clsx(
          'w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors group relative  my-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 capitalize',
          isActiveLink ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50',
          isActiveLink && !isSublink ? 'border-r-2 border-primary-600' : '',
          isActiveLink && isSublink ? 'rounded-r-none' : '',
          isSublink && isExpanded ? 'ml-6 px-2 py-2' : '',
          disabled ? 'text-gray-400 cursor-not-allowed opacity-60' : '',
          !isExpanded ? 'mt-2' : '',
          className,
        );
      }}
      {...rest}
    >
      {({ isActive, isPending }) => (
        <Flex
          dir="col"
          className={clsx(!isExpanded ? 'justify-center' : 'justify-start')}
        >
          {disabled && <Lock className="w-3 h-3 absolute top-1 left-1 text-gray-400" />}
          <Icon className="w-[25px]" size={18} />
          {isExpanded ? (
            <Flex className="ml-3">
              {typeof children === 'function'
                ? children({ isActive, isPending })
                : children}
            </Flex>
          ) : null}
        </Flex>
      )}
    </NavLink>
  );

  if (tooltip) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger>{navLink}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            className="relative z-50 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg"
          >
            {tooltip}
            <Tooltip.Arrow className="fill-gray-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return navLink;
};
